/**
 * appleTv.service.ts
 *
 * The practice's own Apple TV signage — the screens in its waiting rooms.
 * Everything here goes through v5's /apple-tv proxy, which attaches the
 * account's FranchiseSign key server-side. The key is never in the browser, so
 * there is no vendor client here and no base URL to configure.
 *
 * EVERYTHING ARRIVES UNDER `r.data.data`, including the vendor's own responses.
 * The proxy passes FranchiseSign's envelope straight back ({ locations: [...] })
 * so its shapes and messages survive intact — but apiClient's response
 * interceptor rewraps any body with no top-level `data` key as { data: body }.
 * So a vendor payload is double-wrapped by the time it lands here and is read
 * exactly like any other v5 response: ApiResponse<T> and `r.data.data`.
 *
 * Reading `r.data.locations` instead returns undefined, which `?? []` quietly
 * turns into an empty list — a section that looks like it simply has no records.
 * That is precisely how this shipped the first time.
 */
import { apiClient } from './apiClient';
import type { ApiResponse } from '@/types/api.types';

// ─── Domain types (FranchiseSign's own shapes) ───────────────────────────────

export type ScreenStatus = 'online' | 'offline' | 'unknown';
export type MediaType = 'image' | 'video';

export interface AppleTvLocation {
  id: number;
  name: string;
  address: string;
  description: string | null;
  phoneNumber: string | null;
  imageStorageKey: string | null;
  /** Presigned, expires in ~6h — display only, never persist. */
  imageUrl: string | null;
  parentLocationId: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface AppleTvScreen {
  id: number;
  name: string;
  locationId: number;
  screenGroupId: number | null;
  playlistId: number | null;
  deviceId: string | null;
  status: ScreenStatus;
  lastSeen: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AppleTvScreenGroup {
  id: number;
  name: string;
  description: string | null;
  locationId: number;
  playlistId: number | null;
  createdAt: string;
  updatedAt: string;
  screens: AppleTvScreen[];
}

export interface AppleTvSharing {
  owner?: { id: number; firstName: string; lastName: string } | null;
  sharedWithMe?: boolean;
  shareCount?: number;
}

export interface AppleTvPlaylist extends AppleTvSharing {
  id: number;
  name: string;
  description: string | null;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface AppleTvMedia extends AppleTvSharing {
  id: number;
  name: string;
  type: MediaType;
  mimeType: string | null;
  sizeBytes: number | null;
  storageKey: string;
  /** Presigned, expires in ~6h. */
  url: string;
  createdAt: string;
}

export interface AppleTvPlaylistItem {
  id: number;
  playlistId?: number;
  mediaId: number;
  duration: number;
  sortOrder: number;
  startDate: string | null;
  endDate: string | null;
  media?: Pick<AppleTvMedia, 'id' | 'name' | 'type' | 'url'>;
}

export interface AppleTvPlaylistWithItems {
  playlist: AppleTvPlaylist;
  items: AppleTvPlaylistItem[];
}

export interface AppleTvUploadUrl {
  uploadUrl: string;
  storageKey: string;
  contentType: string;
  type?: MediaType;
  expiresIn: number;
}

/** The pairing result, with the device's secret token already stripped by v5. */
export interface AppleTvPairResult {
  success: boolean;
  deviceId: string;
  screenId: number;
  locationId: number;
  playlistId: number | null;
}

export interface AppleTvStatus {
  /** A FranchiseSign key is set up for this account in Module Settings. */
  configured: boolean;
  /** The signed-in user holds the Apple TV Signage permission. */
  canManage: boolean;
}

const BASE = '/apple-tv';

// ─── Status (ours — v5 envelope) ─────────────────────────────────────────────

export function getAppleTvStatus(): Promise<AppleTvStatus> {
  return apiClient
    .get<ApiResponse<AppleTvStatus>>(`${BASE}/status`)
    .then((r) => r.data.data);
}

// ─── Locations (vendor envelope) ─────────────────────────────────────────────

export function listLocations(): Promise<AppleTvLocation[]> {
  return apiClient
    .get<ApiResponse<{ locations: AppleTvLocation[] }>>(`${BASE}/locations`)
    .then((r) => r.data.data.locations ?? []);
}

export interface LocationInput {
  name: string;
  address: string;
  description?: string | null;
  phoneNumber?: string | null;
  imageStorageKey?: string | null;
  parentLocationId?: number | null;
}

export function createLocation(body: LocationInput): Promise<AppleTvLocation> {
  return apiClient
    .post<ApiResponse<{ location: AppleTvLocation }>>(`${BASE}/locations`, body)
    .then((r) => r.data.data.location);
}

export function updateLocation(id: number, body: Partial<LocationInput>): Promise<AppleTvLocation> {
  return apiClient
    .put<ApiResponse<{ location: AppleTvLocation }>>(`${BASE}/locations/${id}`, body)
    .then((r) => r.data.data.location);
}

export function deleteLocation(id: number): Promise<void> {
  return apiClient.delete(`${BASE}/locations/${id}`).then(() => undefined);
}

export function getLocationImageUploadUrl(body: {
  contentType: string;
  fileName?: string;
}): Promise<AppleTvUploadUrl> {
  return apiClient
    .post<ApiResponse<AppleTvUploadUrl>>(`${BASE}/locations/image-upload-url`, body)
    .then((r) => r.data.data);
}

// ─── Screens and groups (vendor envelope) ────────────────────────────────────

export function listScreens(): Promise<AppleTvScreen[]> {
  return apiClient
    .get<ApiResponse<{ screens: AppleTvScreen[] }>>(`${BASE}/screens`)
    .then((r) => r.data.data.screens ?? []);
}

export interface ScreenInput {
  name: string;
  locationId: number;
  screenGroupId?: number | null;
  playlistId?: number | null;
}

export function createScreen(body: ScreenInput): Promise<AppleTvScreen> {
  return apiClient
    .post<ApiResponse<{ screen: AppleTvScreen }>>(`${BASE}/screens`, body)
    .then((r) => r.data.data.screen);
}

export function updateScreen(id: number, body: Partial<ScreenInput>): Promise<AppleTvScreen> {
  return apiClient
    .put<ApiResponse<{ screen: AppleTvScreen }>>(`${BASE}/screens/${id}`, body)
    .then((r) => r.data.data.screen);
}

export function deleteScreen(id: number): Promise<void> {
  return apiClient.delete(`${BASE}/screens/${id}`).then(() => undefined);
}

/**
 * Point one screen at a playlist. The vendor may answer with a `warning` when
 * the screen belongs to a group whose own playlist wins — surfaced to the user
 * rather than swallowed, because otherwise the change looks like it did nothing.
 */
export function assignScreenPlaylist(
  id: number,
  playlistId: number | null,
): Promise<{ screen: AppleTvScreen; warning?: string }> {
  return apiClient
    .put<ApiResponse<{ screen: AppleTvScreen; warning?: string }>>(`${BASE}/screens/${id}/playlist`, { playlistId })
    .then((r) => r.data.data);
}

export function listScreenGroups(): Promise<AppleTvScreenGroup[]> {
  return apiClient
    .get<ApiResponse<{ screenGroups: AppleTvScreenGroup[] }>>(`${BASE}/screen-groups`)
    .then((r) => r.data.data.screenGroups ?? []);
}

export interface ScreenGroupInput {
  name: string;
  locationId: number;
  description?: string | null;
  playlistId?: number | null;
}

export function createScreenGroup(body: ScreenGroupInput): Promise<AppleTvScreenGroup> {
  return apiClient
    .post<ApiResponse<{ screenGroup: AppleTvScreenGroup }>>(`${BASE}/screen-groups`, body)
    .then((r) => r.data.data.screenGroup);
}

export function updateScreenGroup(
  id: number,
  body: Partial<Omit<ScreenGroupInput, 'locationId'>>,
): Promise<AppleTvScreenGroup> {
  return apiClient
    .put<ApiResponse<{ screenGroup: AppleTvScreenGroup }>>(`${BASE}/screen-groups/${id}`, body)
    .then((r) => r.data.data.screenGroup);
}

export function deleteScreenGroup(id: number): Promise<void> {
  return apiClient.delete(`${BASE}/screen-groups/${id}`).then(() => undefined);
}

export function addScreensToGroup(groupId: number, screenIds: number[]): Promise<AppleTvScreenGroup> {
  return apiClient
    .post<ApiResponse<{ screenGroup: AppleTvScreenGroup }>>(`${BASE}/screen-groups/${groupId}/screens`, { screenIds })
    .then((r) => r.data.data.screenGroup);
}

export function removeScreenFromGroup(groupId: number, screenId: number): Promise<AppleTvScreenGroup> {
  return apiClient
    .delete<ApiResponse<{ screenGroup: AppleTvScreenGroup }>>(`${BASE}/screen-groups/${groupId}/screens/${screenId}`)
    .then((r) => r.data.data.screenGroup);
}

// ─── Playlists (vendor envelope) ─────────────────────────────────────────────

export function listPlaylists(): Promise<AppleTvPlaylist[]> {
  return apiClient
    .get<ApiResponse<{ playlists: AppleTvPlaylist[] }>>(`${BASE}/playlists`)
    .then((r) => r.data.data.playlists ?? []);
}

export function getPlaylist(id: number): Promise<AppleTvPlaylistWithItems> {
  return apiClient
    .get<ApiResponse<AppleTvPlaylistWithItems>>(`${BASE}/playlists/${id}`)
    .then((r) => r.data.data);
}

export function createPlaylist(name: string): Promise<AppleTvPlaylist> {
  return apiClient
    .post<ApiResponse<{ playlist: AppleTvPlaylist }>>(`${BASE}/playlists`, { name })
    .then((r) => r.data.data.playlist);
}

export function updatePlaylist(
  id: number,
  body: { name?: string; isDefault?: boolean },
): Promise<AppleTvPlaylist> {
  return apiClient
    .put<ApiResponse<{ playlist: AppleTvPlaylist }>>(`${BASE}/playlists/${id}`, body)
    .then((r) => r.data.data.playlist);
}

export function deletePlaylist(id: number): Promise<void> {
  return apiClient.delete(`${BASE}/playlists/${id}`).then(() => undefined);
}

export function addPlaylistItem(
  playlistId: number,
  body: { mediaId: number; duration?: number; startDate?: string | null; endDate?: string | null },
): Promise<AppleTvPlaylistItem> {
  return apiClient
    .post<ApiResponse<{ item: AppleTvPlaylistItem }>>(`${BASE}/playlists/${playlistId}/items`, body)
    .then((r) => r.data.data.item);
}

export function updatePlaylistItem(
  playlistId: number,
  itemId: number,
  body: { duration?: number; startDate?: string | null; endDate?: string | null },
): Promise<AppleTvPlaylistItem> {
  return apiClient
    .put<ApiResponse<{ item: AppleTvPlaylistItem }>>(`${BASE}/playlists/${playlistId}/items/${itemId}`, body)
    .then((r) => r.data.data.item);
}

export function removePlaylistItem(playlistId: number, itemId: number): Promise<void> {
  return apiClient.delete(`${BASE}/playlists/${playlistId}/items/${itemId}`).then(() => undefined);
}

export function reorderPlaylistItems(
  playlistId: number,
  items: { id: number; sortOrder: number }[],
): Promise<void> {
  return apiClient
    .put(`${BASE}/playlists/${playlistId}/items/reorder`, { items })
    .then(() => undefined);
}

// ─── Media (vendor envelope) ─────────────────────────────────────────────────

export function listMedia(): Promise<AppleTvMedia[]> {
  return apiClient
    .get<ApiResponse<{ media: AppleTvMedia[] }>>(`${BASE}/media`)
    .then((r) => r.data.data.media ?? []);
}

export function getMediaUploadUrl(body: {
  name: string;
  contentType: string;
  fileName?: string;
}): Promise<AppleTvUploadUrl> {
  return apiClient
    .post<ApiResponse<AppleTvUploadUrl>>(`${BASE}/media/upload-url`, body)
    .then((r) => r.data.data);
}

export function registerMedia(body: { name: string; storageKey: string }): Promise<AppleTvMedia> {
  return apiClient
    .post<ApiResponse<{ media: AppleTvMedia }>>(`${BASE}/media`, body)
    .then((r) => r.data.data.media);
}

export function deleteMedia(id: number): Promise<void> {
  return apiClient.delete(`${BASE}/media/${id}`).then(() => undefined);
}

/**
 * PUT the file straight to the presigned storage URL, reporting progress.
 *
 * Deliberately NOT through apiClient: the URL is already signed, and sending our
 * Authorization header with it makes storage reject the upload. It also keeps
 * large video files off our own API entirely.
 */
export function uploadToStorage(
  uploadUrl: string,
  contentType: string,
  file: File,
  onProgress?: (pct: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`Upload failed (${xhr.status})`));
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.send(file);
  });
}

// ─── Device pairing (vendor envelope) ────────────────────────────────────────

export function pairDevice(body: {
  pairingCode: string;
  screenName: string;
  locationId: number;
  screenGroupId?: number | null;
  playlistId?: number | null;
}): Promise<AppleTvPairResult> {
  return apiClient
    .post<ApiResponse<AppleTvPairResult>>(`${BASE}/devices/pair`, body)
    .then((r) => r.data.data);
}

export function unpairDevice(deviceId: string): Promise<void> {
  return apiClient.post(`${BASE}/devices/unpair`, { deviceId }).then(() => undefined);
}
